import * as React from 'react';

export interface UseBhanzuBuddyToggleOpenProps {
  isOpen: boolean;
  isControlledComp: boolean;
  onToggleOpen?: (isOpen: boolean) => void;
  showQuickChallenge: {
    open: boolean;
    question: string;
    category: string;
    color: string;
  };
  setShowQuickChallenge: (value: {
    open: boolean;
    question: string;
    category: string;
    color: string;
  }) => void;
  setChatState: (state: 'closed' | 'opening' | 'open' | 'closing') => void;
  hasScrolledOnOpen: React.RefObject<boolean>;
  setInternalIsOpen: (value: boolean) => void;
}

export const useBhanzuBuddyToggleOpen = ({
  isOpen,
  isControlledComp,
  onToggleOpen,
  showQuickChallenge,
  setShowQuickChallenge,
  setChatState,
  hasScrolledOnOpen,
  setInternalIsOpen,
}: UseBhanzuBuddyToggleOpenProps) => {
  const animationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(() => {
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, []);

  const handleToggleOpen = React.useCallback(() => {
    const newIsOpen = !isOpen;

    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    if (newIsOpen && showQuickChallenge.open) {
      setShowQuickChallenge({
        open: false,
        question: '',
        category: '',
        color: 'underwater-fern-green',
      });
    }

    if (newIsOpen) {
      setChatState('opening');
      animationTimerRef.current = setTimeout(() => setChatState('open'), 0);
    } else {
      setChatState('closing');
      animationTimerRef.current = setTimeout(() => setChatState('closed'), 0);
      hasScrolledOnOpen.current = false;
    }

    if (!isControlledComp) {
      setInternalIsOpen(newIsOpen);
    }

    onToggleOpen?.(newIsOpen);
  }, [
    isOpen,
    isControlledComp,
    onToggleOpen,
    showQuickChallenge.open,
    setChatState,
    setShowQuickChallenge,
    hasScrolledOnOpen,
    setInternalIsOpen,
  ]);

  return {
    handleToggleOpen,
  };
};
